import * as React from 'react';
import { Container, Grid, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Panel from '../panel/Panel';
import { observer } from 'mobx-react-lite';
import { useStores } from '../../stores';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import moment from 'moment';
import { getLocale } from '../../utils/localeUtils';

const assignmentLabels = (t: any): Record<string, string> => ({
    All: t("all"),
    Publishers: t("publishers"),
    "Regular Pioneer": t("Regular Pioneer"),
    "Auxiliary Pioneer": t("Auxiliary Pioneer"),
    "Special Pioneer": t("Special Pioneer"),
});

const ReportsStatisticsPage = observer(() => {
    const { t } = useTranslation();
    const { reportStore, serverStore } = useStores();
    const [from, setFrom] = React.useState<Date>(moment().startOf('month').subtract(12, 'month').toDate());
    const [to, setTo] = React.useState<Date>(moment().startOf('month').toDate());
    const [statistics, setStatistics] = React.useState<any[]>([]);

    const labels = assignmentLabels(t);

    React.useEffect(() => {
        reportStore.getReportsStatistics(from, to, (data) => {
            setStatistics(data);
        });
    }, [reportStore, from, to]);

    const normalizeMonth = (value: Date | null) => {
        if (!value) {
            return moment().startOf('month').toDate();
        }
        return moment(value).startOf('month').toDate();
    }

    const handleFromChange = (value: Date | null) => {
        const normalized = normalizeMonth(value);
        setFrom(normalized);
        if (moment(normalized).isAfter(moment(to))) {
            setTo(normalized);
        }
    };

    const handleToChange = (value: Date | null) => {
        const normalized = normalizeMonth(value);
        if (moment(normalized).isBefore(moment(from))) {
            setFrom(normalized);
        }
        setTo(normalized);
    };

    return (
        <Panel title={t("statistics")}>
            <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
                <Paper sx={{ p: 3 }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns} locale={getLocale()}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <DatePicker
                                    views={['year', 'month']}
                                    label={t("from")}
                                    value={from}
                                    disableMaskedInput
                                    onChange={handleFromChange}
                                    renderInput={(params) => (
                                        <TextField {...params} margin="normal" fullWidth />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DatePicker
                                    views={['year', 'month']}
                                    label={t("to")}
                                    value={to}
                                    disableMaskedInput
                                    onChange={handleToChange}
                                    renderInput={(params) => (
                                        <TextField {...params} margin="normal" fullWidth />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </LocalizationProvider>
                </Paper>
            </Container>
            <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
                {serverStore.serverRequestLoading && <LinearProgress />}
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t("service_assignment")}</TableCell>
                                <TableCell align="right">{t("hours")}</TableCell>
                                <TableCell align="right">{t("bible_studies")}</TableCell>
                                <TableCell align="right">{t("publishers")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {statistics.map((row) => (
                                <TableRow key={row.serviceAssignment}>
                                    <TableCell>{labels[row.serviceAssignment] || row.serviceAssignment}</TableCell>
                                    <TableCell align="right">{row.hours ?? 0}</TableCell>
                                    <TableCell align="right">{row.bibleStudies ?? 0}</TableCell>
                                    <TableCell align="right">{row.publisherCount ?? 0}</TableCell>
                                </TableRow>
                            ))}
                            {!statistics.length && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        {t("no_data")}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Container>
        </Panel>
    );
});

export default ReportsStatisticsPage;
